import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import {
  Flame, Trophy, Shield, ArrowUpRight, Copy, ExternalLink,
  Rocket, Coins, TrendingDown, CircleDollarSign, Waves,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Panel = ({ children, className = '', glow = false, accent, style = {}, ...props }) => {
  const ac = accent || 'rgba(255,255,255,0.06)';
  return (
    <div className={`relative overflow-hidden ${className}`} {...props}
      style={{
        background: glow ? 'rgba(12,12,12,0.9)' : 'rgba(8,8,8,0.85)',
        border: `1px solid ${ac}`, backdropFilter: 'blur(10px)',
        boxShadow: glow ? `0 0 25px ${ac}, inset 0 0 15px rgba(255,255,255,0.01)` : 'inset 0 0 10px rgba(255,255,255,0.01)',
        ...style,
      }}>
      <div className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l z-10" style={{ borderColor: ac }} />
      <div className="absolute top-0 right-0 w-2.5 h-2.5 border-t border-r z-10" style={{ borderColor: ac }} />
      <div className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b border-l z-10" style={{ borderColor: ac }} />
      <div className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r z-10" style={{ borderColor: ac }} />
      {children}
    </div>
  );
};

const SplitBar = ({ dev, community }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: false, margin: '-20px' });
  return (
    <div ref={ref} className="flex h-4 overflow-hidden relative" data-testid="split-bar"
      style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
      <motion.div initial={{ width: 0 }} animate={inView ? { width: `${dev}%` } : { width: 0 }}
        transition={{ duration: 0.9 }}
        className="h-full flex items-center justify-end pr-2 text-[9px] font-bold"
        style={{ background: 'linear-gradient(90deg, rgba(239,68,68,0.5), rgba(239,68,68,0.9))',
                 fontFamily: "'Cinzel', serif", color: '#fff' }}
        data-testid="split-bar-dev">DEV {dev}%</motion.div>
      <motion.div initial={{ width: 0 }} animate={inView ? { width: `${community}%` } : { width: 0 }}
        transition={{ duration: 0.9, delay: 0.15 }}
        className="h-full flex items-center justify-start pl-2 text-[9px] font-bold"
        style={{ background: 'linear-gradient(90deg, rgba(234,179,8,0.9), rgba(234,179,8,0.5))',
                 fontFamily: "'Cinzel', serif", color: '#111' }}
        data-testid="split-bar-community">COMMUNITY {community}%</motion.div>
    </div>
  );
};

const shortWallet = (w) => (w && w.length > 12 ? `${w.slice(0, 5)}…${w.slice(-4)}` : (w || ''));
const fmtSol = (n) => (typeof n === 'number' ? n.toLocaleString(undefined, { maximumFractionDigits: 4 }) : '—');
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return iso; } };

const FairLaunchPage = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [balance, setBalance] = useState(null);
  const [payouts, setPayouts] = useState({ items: [], totals: { count: 0, sol: 0, unique_recipients: 0 } });
  const [milestones, setMilestones] = useState([]);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [cRes, bRes, pRes, mRes] = await Promise.all([
          fetch(`${API}/api/fair-launch/config`),
          fetch(`${API}/api/fair-launch/wallet-balance`),
          fetch(`${API}/api/fair-launch/payouts?limit=25`),
          fetch(`${API}/api/arcade/descent/milestones?limit=8&include_demo=false`),
        ]);
        if (!alive) return;
        setConfig(cRes.ok ? await cRes.json() : null);
        setBalance(bRes.ok ? await bRes.json() : null);
        setPayouts(pRes.ok ? await pRes.json() : { items: [], totals: {} });
        const mJson = mRes.ok ? await mRes.json() : { items: [] };
        setMilestones(mJson.items || []);
      } finally { if (alive) setLoading(false); }
    };
    load();
    const t = setInterval(load, 45000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const dev = config?.split?.dev_pct ?? 60;
  const community = config?.split?.community_pct ?? 40;
  const wallet = config?.creator_rewards_wallet || balance?.wallet || '';
  const isLaunched = !!config?.is_launched;
  const previewMode = !!config?.preview_mode;
  const pumpUrl = config?.pump_fun_url;

  const copyWallet = async () => {
    if (!wallet) return;
    try {
      await navigator.clipboard.writeText(wallet);
      setCopied(true); setTimeout(() => setCopied(false), 1200);
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen text-white relative" data-testid="fair-launch-page" style={{ background: '#080808' }}>
      <div className="fixed inset-0 z-0" style={{ background: 'radial-gradient(ellipse at 50% 25%, #111 0%, #0a0a0a 40%, #080808 100%)' }} />
      <div className="fixed inset-0 z-[1] pointer-events-none">
        <motion.div className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at 30% 30%, rgba(239,68,68,0.05) 0%, transparent 45%)' }}
          animate={{ opacity: [0.4, 0.8, 0.4] }} transition={{ duration: 5, repeat: Infinity }} />
        <motion.div className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at 70% 60%, rgba(234,179,8,0.04) 0%, transparent 40%)' }}
          animate={{ opacity: [0.3, 0.7, 0.3] }} transition={{ duration: 6, repeat: Infinity, delay: 1 }} />
      </div>
      <div className="fixed inset-0 z-[60] pointer-events-none mix-blend-overlay" style={{
        background: 'linear-gradient(rgba(18,16,16,0) 50%, rgba(0,0,0,0.15) 50%)', backgroundSize: '100% 4px', opacity: 0.08,
      }} />

      <BackButton to="/" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 pt-20 pb-16">
        <div className="mb-10">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="text-[10px] uppercase tracking-[0.5em] mb-1"
            style={{ fontFamily: "'Cinzel', serif", color: 'rgba(239,68,68,0.55)' }}>
            No Presale. No Team Bags. No Promises.
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, type: 'spring', stiffness: 100 }}
            className="relative text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-wider inline-block"
            style={{ fontFamily: "'Cinzel Decorative', 'Cinzel', serif" }} data-testid="fair-launch-title">
            <span className="absolute inset-0" style={{ color: 'transparent', WebkitTextStroke: '2px rgba(255,255,255,0.08)', filter: 'blur(6px)' }}>FAIR LAUNCH</span>
            <span className="relative" style={{
              background: 'linear-gradient(180deg, #ffffff 0%, #e5e5e5 20%, #d4d4d4 40%, #a3a3a3 60%, #737373 85%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              filter: 'drop-shadow(0 0 15px rgba(255,255,255,0.15)) drop-shadow(0 0 40px rgba(239,68,68,0.1))',
            }}>FAIR LAUNCH</span>
          </motion.h1>
          <motion.div initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-3 h-[1px]"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(239,68,68,0.35), rgba(234,179,8,0.25), rgba(255,255,255,0.1), transparent)' }} />
          <p className="mt-4 text-xs md:text-sm max-w-2xl leading-relaxed"
            style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.5)' }}>
            <span style={{ color: '#eab308' }}>$GFG</span> launches on{' '}
            <a href="https://pump.fun" target="_blank" rel="noreferrer" className="underline decoration-dotted hover:text-red-300 transition"
              data-testid="pump-fun-link" style={{ color: 'rgba(255,255,255,0.7)' }}>Pump.fun</a>{' '}
            — bonded curve, no allocation, no team wallets. The dev&apos;s <em>creator rewards</em> (paid in SOL by Pump.fun on every trade) are split each month:
            {' '}<span style={{ color: '#ef4444' }}>{dev}% dev / {community}% community</span> payouts to top Descent + arcade players.
          </p>
          {previewMode ? (
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 text-[10px]"
              style={{ background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.3)',
                       fontFamily: "'Cinzel', serif", color: '#eab308', letterSpacing: '0.15em' }}
              data-testid="preview-mode-badge">
              PREVIEW MODE · CREATOR REWARDS ACCRUE · GAMEPLAY DEMO-ONLY
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="status-grid">
          <Panel className="p-4" accent={isLaunched ? 'rgba(16,185,129,0.25)' : 'rgba(234,179,8,0.2)'}>
            <div className="flex items-center gap-2 relative z-10">
              <Rocket className="w-4 h-4" style={{ color: isLaunched ? '#10b981' : '#eab308' }} />
              <p className="text-[9px] tracking-[0.15em] uppercase" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,255,255,0.4)' }}>Launch Status</p>
            </div>
            <p className="text-xl font-bold mt-1 relative z-10"
              style={{ fontFamily: "'Impact', sans-serif", color: isLaunched ? '#10b981' : '#eab308' }}
              data-testid="launch-status">
              {isLaunched ? 'LIVE ON PUMP.FUN' : 'AWAITING LAUNCH'}
            </p>
            {isLaunched && pumpUrl ? (
              <a href={pumpUrl} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-[10px] mt-1 hover:text-yellow-300 transition"
                style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.5)' }}
                data-testid="pump-fun-launch-link">View on Pump.fun <ExternalLink className="w-3 h-3" /></a>
            ) : (
              <p className="text-[10px] mt-1" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.25)' }}>
                Bookmark — the link drops here on launch day.
              </p>
            )}
          </Panel>

          <Panel className="p-4" accent="rgba(239,68,68,0.22)">
            <div className="flex items-center gap-2 relative z-10">
              <CircleDollarSign className="w-4 h-4" style={{ color: '#ef4444' }} />
              <p className="text-[9px] tracking-[0.15em] uppercase" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,255,255,0.4)' }}>Creator-Rewards Vault</p>
            </div>
            <p className="text-xl font-bold mt-1 relative z-10" style={{ fontFamily: "'Impact', sans-serif", color: '#fff' }}
              data-testid="creator-vault-balance">
              {loading ? '…' : `${fmtSol(balance?.sol)} SOL`}
            </p>
            <p className="text-[10px] mt-1 relative z-10" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.25)' }}>
              Community share ({community}%): {loading ? '…' : `${fmtSol((balance?.sol || 0) * community / 100)} SOL`}
            </p>
          </Panel>

          <Panel className="p-4" accent="rgba(234,179,8,0.2)">
            <div className="flex items-center gap-2 relative z-10">
              <Trophy className="w-4 h-4" style={{ color: '#eab308' }} />
              <p className="text-[9px] tracking-[0.15em] uppercase" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,255,255,0.4)' }}>Payouts Sent</p>
            </div>
            <p className="text-xl font-bold mt-1 relative z-10" style={{ fontFamily: "'Impact', sans-serif", color: '#eab308' }}
              data-testid="payouts-total">
              {loading ? '…' : `${fmtSol(payouts?.totals?.sol)} SOL`}
            </p>
            <p className="text-[10px] mt-1 relative z-10" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.25)' }}>
              {payouts?.totals?.count || 0} payouts · {payouts?.totals?.unique_recipients || 0} unique winners
            </p>
          </Panel>
        </div>

        <Panel className="p-6 mb-6" accent="rgba(255,255,255,0.06)" data-testid="split-panel">
          <div className="flex items-center justify-between mb-4 relative z-10">
            <div className="flex items-center gap-2">
              <Waves className="w-4 h-4" style={{ color: '#ef4444' }} />
              <h2 className="text-sm font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,255,255,0.7)' }}>Creator Rewards Split</h2>
            </div>
            <p className="text-[9px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.25)' }}>paid in SOL · settled monthly</p>
          </div>
          <div className="relative z-10"><SplitBar dev={dev} community={community} /></div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 relative z-10">
            <div className="p-3" style={{ background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.15)' }}>
              <p className="text-[10px] font-bold mb-1" style={{ fontFamily: "'Cinzel', serif", color: '#ef4444' }}>DEV · {dev}%</p>
              <p className="text-[11px] leading-relaxed" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.4)' }}>
                Covers Claude, hosting, Emergent, RPCs, and continued game/AI development. No lockups.
              </p>
            </div>
            <div className="p-3" style={{ background: 'rgba(234,179,8,0.04)', border: '1px solid rgba(234,179,8,0.2)' }}>
              <p className="text-[10px] font-bold mb-1" style={{ fontFamily: "'Cinzel', serif", color: '#eab308' }}>COMMUNITY · {community}%</p>
              <p className="text-[11px] leading-relaxed" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.4)' }}>
                Distributed each month to the top Descent survivors + arcade grinders. Payouts posted on-chain, tx signatures below.
              </p>
            </div>
          </div>
        </Panel>

        <Panel className="p-5 mb-6" accent="rgba(255,255,255,0.06)" data-testid="creator-wallet-panel">
          <div className="flex items-center justify-between gap-3 relative z-10">
            <div className="flex items-center gap-2 min-w-0">
              <Shield className="w-4 h-4 flex-shrink-0" style={{ color: '#10b981' }} />
              <div className="min-w-0">
                <p className="text-[9px] tracking-[0.15em] uppercase" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,255,255,0.4)' }}>
                  Creator-Rewards Wallet · Public · Verifiable
                </p>
                <p className="text-xs font-mono truncate" data-testid="creator-wallet-address"
                  style={{ color: 'rgba(255,255,255,0.85)' }}>
                  {wallet || '— unset —'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={copyWallet}
                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] transition"
                style={{ fontFamily: "'Courier New', monospace", background: 'rgba(255,255,255,0.04)',
                         border: '1px solid rgba(255,255,255,0.1)',
                         color: copied ? '#10b981' : 'rgba(255,255,255,0.6)' }}
                data-testid="copy-wallet-btn"><Copy className="w-3 h-3" />{copied ? 'Copied' : 'Copy'}</button>
              <a href={wallet ? `https://solscan.io/account/${wallet}` : '#'}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] transition hover:text-yellow-300"
                style={{ fontFamily: "'Courier New', monospace", background: 'rgba(255,255,255,0.04)',
                         border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}
                data-testid="solscan-link">Solscan <ExternalLink className="w-3 h-3" /></a>
            </div>
          </div>
        </Panel>

        <Panel className="p-6 mb-6" accent="rgba(239,68,68,0.1)" data-testid="how-it-works">
          <h2 className="text-sm font-bold mb-4 flex items-center gap-2 relative z-10" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,255,255,0.7)' }}>
            <Coins className="w-4 h-4" style={{ color: '#ef4444' }} /> How the Fair Launch Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 relative z-10">
            {[
              { icon: Rocket, color: '#ef4444', title: '1 · Bond on Pump.fun', desc: '$GFG launches on the Pump.fun bonded curve. Anyone can buy from block one. No presale, no team wallets, no whitelist.' },
              { icon: CircleDollarSign, color: '#eab308', title: '2 · Rewards Accrue in SOL', desc: 'Every trade pays creator rewards in SOL directly to the public dev wallet above. Balance is live — refresh to verify.' },
              { icon: Trophy, color: '#10b981', title: `3 · ${community}% to Winners`, desc: `Each month, ${community}% of accrued SOL is paid out to the top Descent + arcade players once the full arcade goes live. Every transfer is receipted here.` },
            ].map((s) => (
              <div key={s.title} className="p-3" style={{ background: `${s.color}05`, border: `1px solid ${s.color}18` }}>
                <s.icon className="w-5 h-5 mb-2" style={{ color: s.color }} />
                <h3 className="text-xs font-bold mb-1" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,255,255,0.75)' }}>{s.title}</h3>
                <p className="text-[10px] leading-relaxed" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.35)' }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </Panel>

        <Panel className="p-5 mb-6" accent="rgba(234,179,8,0.15)" data-testid="payouts-panel">
          <h2 className="text-sm font-bold mb-3 flex items-center gap-2 relative z-10" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,255,255,0.7)' }}>
            <Trophy className="w-4 h-4" style={{ color: '#eab308' }} /> Monthly Payout Ledger
          </h2>
          <div className="relative z-10">
            {(payouts.items || []).length === 0 ? (
              <div className="p-6 text-center" style={{ background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.08)' }}>
                <p className="text-xs" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.3)' }}>
                  No payouts recorded yet. The first monthly distribution drops after the full-arcade launch.
                </p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="payouts-list">
                {(payouts.items || []).map((p) => (
                  <div key={p.id || p.tx_signature} className="flex items-center justify-between gap-3 px-3 py-2"
                    style={{ background: 'rgba(234,179,8,0.03)', border: '1px solid rgba(234,179,8,0.1)' }}>
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      {p.rank ? (
                        <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-[10px] font-bold"
                          style={{ fontFamily: "'Impact', sans-serif", background: 'rgba(234,179,8,0.15)', border: '1px solid rgba(234,179,8,0.35)', color: '#eab308' }}>{p.rank}</span>
                      ) : (<span className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-[9px]" style={{ color: 'rgba(255,255,255,0.3)' }}>—</span>)}
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-mono truncate" style={{ color: 'rgba(255,255,255,0.85)' }}>{shortWallet(p.recipient)}</p>
                        <p className="text-[9px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.3)' }}>{p.month} · {fmtDate(p.created_at)} · {p.reason || 'payout'}</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold" style={{ fontFamily: "'Impact', sans-serif", color: '#eab308' }}>{fmtSol(p.sol)} SOL</p>
                      {p.explorer_url ? (
                        <a href={p.explorer_url} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[9px] hover:text-yellow-300 transition"
                          style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.4)' }}>tx <ExternalLink className="w-2.5 h-2.5" /></a>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Panel>

        <Panel className="p-5 mb-6" accent="rgba(255,255,255,0.06)" data-testid="descent-wall">
          <div className="flex items-center justify-between mb-3 relative z-10">
            <div className="flex items-center gap-2">
              <Flame className="w-4 h-4" style={{ color: '#ef4444' }} />
              <h2 className="text-sm font-bold" style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,255,255,0.7)' }}>Wall of Immortals · Descent Floor 10 Clears</h2>
            </div>
            <p className="text-[9px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.25)' }}>
              {previewMode ? 'off-chain during preview' : 'on-chain via SPL Memo'}
            </p>
          </div>
          <div className="relative z-10">
            {milestones.length === 0 ? (
              <div className="p-6 text-center" style={{ background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.08)' }}>
                <p className="text-xs" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.3)' }}>
                  No live-mode clears yet. Preview-mode demo runs are excluded from this leaderboard.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="milestones-list">
                {milestones.map((m) => (
                  <div key={m.id || m.tx_signature || m.created_at} className="flex items-center justify-between gap-3 px-3 py-2"
                    style={{ background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.12)' }}>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-mono truncate" style={{ color: 'rgba(255,255,255,0.85)' }}>{shortWallet(m.wallet_address)}</p>
                      <p className="text-[9px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.3)' }}>
                        Score {m.score?.toLocaleString?.() || m.score} · {fmtDate(m.created_at)}
                      </p>
                    </div>
                    {m.explorer_url ? (
                      <a href={m.explorer_url} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[9px] hover:text-red-300 transition"
                        style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.4)' }}>memo <ExternalLink className="w-2.5 h-2.5" /></a>
                    ) : (<span className="text-[9px]" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.2)' }}>off-chain</span>)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Panel>

        <div className="text-center mb-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <motion.button onClick={() => navigate('/arcade')}
            className="inline-flex items-center gap-2 px-8 py-3 text-sm font-bold tracking-wider uppercase transition-all"
            style={{ fontFamily: "'Cinzel', serif", background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444' }}
            whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
            data-testid="play-arcade-cta">
            {previewMode ? 'Try the Demo' : 'Play to Qualify'} <ArrowUpRight className="w-4 h-4" />
          </motion.button>
          {pumpUrl ? (
            <motion.a href={pumpUrl} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3 text-sm font-bold tracking-wider uppercase transition-all"
              style={{ fontFamily: "'Cinzel', serif", background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.3)', color: '#eab308' }}
              whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              data-testid="pump-fun-cta">Buy on Pump.fun <ExternalLink className="w-4 h-4" /></motion.a>
          ) : (
            <motion.button disabled
              className="inline-flex items-center gap-2 px-8 py-3 text-sm font-bold tracking-wider uppercase cursor-not-allowed opacity-50"
              style={{ fontFamily: "'Cinzel', serif", background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.15)', color: '#eab308' }}
              data-testid="pump-fun-cta-disabled">Pump.fun Link Coming Soon</motion.button>
          )}
        </div>

        <Panel className="p-5 mb-6" accent="rgba(255,255,255,0.04)">
          <h3 className="text-[10px] font-bold mb-1 relative z-10 flex items-center gap-2"
            style={{ fontFamily: "'Cinzel', serif", color: 'rgba(255,255,255,0.35)' }}>
            <TrendingDown className="w-3 h-3" /> Disclaimer
          </h3>
          <p className="text-[9px] leading-relaxed relative z-10" style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.2)' }}>
            $GFG is a community meme launched on Pump.fun with zero pre-allocation, zero team lockups, and zero expectation of financial return.
            Creator rewards are a Pump.fun feature — the SOL paid on trades to the dev wallet — not a legal promise. The community share is discretionary and reflects the dev&apos;s public commitment shown here. Always do your own research. Not financial advice.
          </p>
        </Panel>

        <p className="text-center text-[9px] tracking-[0.2em] uppercase"
          style={{ fontFamily: "'Courier New', monospace", color: 'rgba(255,255,255,0.08)' }}>
          Gas Free Gremlins · Fair Launch · {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
};

export default FairLaunchPage;
